import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { ShoppingBag } from "lucide-react";
import { SafeImage } from "../../../components/common/SafeImage";

interface IntegrationItem {
    nameKey: string;
    src?: string;
    fallbackNode?: React.ReactNode;
    statusKey?: string;
}

const cdnBase = (process.env.CDN_URL || "https://cdn.dashbal.com").replace(/\/$/, "");

const ITEMS: IntegrationItem[] = [
    { nameKey: "channels.shopify", src: `${cdnBase}/logos/channel_logos/shopify/shopify_primary.png` },
    { nameKey: "channels.meta", src: `${cdnBase}/logos/channel_logos/meta/meta_primary.png` },
    { nameKey: "channels.tiktok", src: `${cdnBase}/logos/channel_logos/tiktok/tiktok_primary.png` },
    { nameKey: "channels.woocommerce", src: `${cdnBase}/logos/channel_logos/woocommerce/Woo_primary.png` },
    { nameKey: "channels.google", src: `${cdnBase}/logos/channel_logos/google/google_primary.svg`, statusKey: "landing.integrations.soon" },
    { nameKey: "channels.amazon", fallbackNode: <ShoppingBag className="w-8 h-8 text-neutral-400" />, statusKey: "landing.integrations.soon" },
];

export const IntegrationsRow = () => {
    const { t } = useTranslation("common");

    return (
        <section id="integrations" className="py-20 sm:py-28">
            <div className="container mx-auto px-4 sm:px-6">
                <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                    <div className="lg:col-span-5">
                        <p className="text-xs font-semibold tracking-[0.2em] uppercase text-accent-primary mb-3">
                            {t("landing.integrations.eyebrow")}
                        </p>
                        <h2 className="text-3xl sm:text-4xl font-bold font-display text-text-primary mb-4">
                            {t("landing.integrations.title")}
                        </h2>
                        <p className="text-base sm:text-lg text-neutral-600 dark:text-neutral-400 leading-relaxed mb-4">
                            {t("landing.integrations.desc")}
                        </p>
                        <p className="text-sm text-neutral-500 dark:text-neutral-500">
                            {t("landing.integrations.note")}
                        </p>
                    </div>

                    <div className="lg:col-span-7">
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
                            {ITEMS.map((item, i) => (
                                <motion.div
                                    key={item.nameKey}
                                    initial={{ opacity: 0, y: 12 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-40px" }}
                                    transition={{ duration: 0.35, delay: i * 0.06 }}
                                    className="relative bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-xl p-5 sm:p-6 flex flex-col items-center justify-center text-center hover:border-accent-primary/40 transition-colors min-h-[130px]"
                                >
                                    {item.statusKey && (
                                        <span className="absolute top-2 right-2 text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-neutral-100 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400">
                                            {t(item.statusKey)}
                                        </span>
                                    )}
                                    <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center mb-3">
                                        {item.src ? (
                                            <SafeImage
                                                src={item.src}
                                                alt={t(item.nameKey) as string}
                                                className="w-full h-full object-contain"
                                                fallback={item.fallbackNode || <ShoppingBag className="w-8 h-8 text-neutral-400" />}
                                            />
                                        ) : (
                                            item.fallbackNode
                                        )}
                                    </div>
                                    <p className="text-sm font-medium text-text-primary">
                                        {t(item.nameKey)}
                                    </p>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};
